import { Text } from '@cretesuite/design-system'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { useEffect, useRef } from 'react'
import { useNetwork } from '../hooks/useNetwork'

const BANNER_HEIGHT_VAR = '--offline-banner-height'

/**
 * Fixed banner shown above the tab bar while the device is offline.
 * Toggles a `body.app-offline` class so pages can reserve bottom space (see
 * `app.css`) and the banner never covers content.
 */
export const OfflineBanner = () => {
  const { isOnline } = useNetwork()
  const { _ } = useLingui()
  const bannerRef = useRef<HTMLOutputElement>(null)

  useEffect(() => {
    document.body.classList.toggle('app-offline', !isOnline)
    return () => document.body.classList.remove('app-offline')
  }, [isOnline])

  // Publish the banner's real height (the text wraps on narrow screens) so
  // CSS can push anchored page footers above it instead of covering them.
  useEffect(() => {
    const banner = bannerRef.current
    if (!banner) return

    const publishHeight = () => {
      document.body.style.setProperty(
        BANNER_HEIGHT_VAR,
        `${banner.offsetHeight}px`,
      )
    }
    publishHeight()
    const observer = new ResizeObserver(publishHeight)
    observer.observe(banner)

    return () => {
      observer.disconnect()
      document.body.style.removeProperty(BANNER_HEIGHT_VAR)
    }
  }, [isOnline])

  if (isOnline) return null

  return (
    <output ref={bannerRef} className='offline-banner' aria-live='polite'>
      <Text variant='body' className='offline-banner__text'>
        {_(
          msg`You're offline. Your changes are saved and will sync when you reconnect.`,
        )}
      </Text>
    </output>
  )
}
